import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button as ButtonComponent } from '@/components/ui/button'
import {
    Select as SelectComponent,
    SelectContent as SelectContentComponent,
    SelectItem as SelectItemComponent,
    SelectTrigger as SelectTriggerComponent,
    SelectValue as SelectValueComponent,
} from '@/components/ui/select'
import ConfirmationDialogComponent from '@/components/common/ConfirmationDialog'
import { MODES, MODE_LABELS, eventLabel } from '@/utils/pixel-warmup'
import {
    ATTENTION_FALLBACK_TEXT,
    AUTOMATED_NOT_ALLOWED_NOTE,
    PAUSED_CAMPAIGN_NOTE,
    checkReasonText,
    lastCheckIsNewerThanCount,
    failureText,
    proposalText,
    warmupStateLabel,
    warmupStateNote,
} from '@/utils/pixel-warmup-panel'
import { useWarmupPanel } from './hooks/useWarmupPanel'
import WarmupCancelDialog from './WarmupCancelDialog'
import WarmupHeldRows, { HeldRow } from './WarmupHeldRows'
import WarmupHistory from './WarmupHistory'
import WarmupHoldoutNote from './WarmupHoldoutNote'

const Button = ButtonComponent as React.ComponentType<any>
const Select = SelectComponent as React.ComponentType<any>
const SelectContent = SelectContentComponent as React.ComponentType<any>
const SelectItem = SelectItemComponent as React.ComponentType<any>
const SelectTrigger = SelectTriggerComponent as React.ComponentType<any>
const SelectValue = SelectValueComponent as React.ComponentType<any>
const ConfirmationDialog = ConfirmationDialogComponent as React.ComponentType<any>

interface WarmupPanelProps {
    /** The ad campaign (database id) whose warm-up is shown. */
    adCampaignId: string
    /** The licence the Ads Manager is opened for; part of the address of the ad set editor. */
    licenceId: string
    formatAmount: (minor: number) => string
    /** The entry of this campaign from the batched read: its warm-up, holdout and notice. */
    entry?: { warmup?: any; holdout?: any; is_owner?: boolean } | null
    /** Reads the warm-ups of the page again. */
    onRefresh: () => Promise<void>
    /** Takes over a warm-up that an owner action answered with. */
    onWarmupChanged: (adCampaignId: string, warmup: any) => void
}

const formatWhen = (value?: string | null) => (value ? new Date(value).toLocaleString() : '')

const needsAttention = (warmup: any) =>
    warmup.can.confirm ||
    warmup.can.retry ||
    warmup.state === 'failed' ||
    warmup.attention.some((item: any) => !item.resolved_at)

const metaAdSetLink = (externalId: string) =>
    `https://adsmanager.facebook.com/adsmanager/manage/adsets?selected_adset_ids=${encodeURIComponent(externalId)}`

/**
 * The warm-up of one ad campaign: where it stands, what the owner can do about it, and what needs their attention.
 * Every action the panel offers comes from the server's `can` flags for the current state, switches and halt; the
 * server checks them again when the action is called. Nothing is shown for a campaign without a warm-up.
 */
const WarmupPanel: React.FC<WarmupPanelProps> = ({
    adCampaignId,
    licenceId,
    formatAmount,
    entry = null,
    onRefresh,
    onWarmupChanged,
}) => {
    const navigate = useNavigate()
    // Check again is for the owner; the server says whether the reader is
    const mayRecheck = entry?.is_owner === true
    const warmup = entry?.warmup || null
    const panel = useWarmupPanel(adCampaignId, {
        warmup,
        refresh: onRefresh,
        applyWarmup: onWarmupChanged,
        formatAmount,
    })
    const [open, setOpen] = useState<boolean | null>(null)
    const [confirming, setConfirming] = useState(false)
    const [cancelling, setCancelling] = useState(false)
    const [choiceRequired, setChoiceRequired] = useState(false)
    const [removing, setRemoving] = useState<HeldRow | null>(null)

    if (!warmup) return null

    const expanded = open ?? needsAttention(warmup)
    const running = warmup.state === 'expanding' && warmup.expansion.lease_live
    const progress = warmup.progress
    const olderCount = lastCheckIsNewerThanCount(progress)
    const openAttention = warmup.attention.filter((item: any) => !item.resolved_at)
    const adSetIdOf = (externalId?: string | null) => {
        if (!externalId) return null
        if (warmup.warmup_ad_set?.external_id === externalId) return warmup.warmup_ad_set.id
        return warmup.held_rows.find((row: HeldRow) => row.external_id === externalId)?.id || null
    }
    const warmupNeedsChoice =
        choiceRequired || (warmup.warmup_ad_set?.status === 'PAUSED' && ['failed', 'expanding'].includes(warmup.state))
    const editHref = (row: HeldRow) =>
        `/campaigns/media-buying/${licenceId}/ads-manager/${adCampaignId}/ad-sets/${row.id}/edit`

    const failure = failureText(warmup.expansion.failure_reason)

    return (
        <section
            aria-label="Warm-up"
            data-testid="warmup-panel"
            className="tw:sticky tw:left-0 tw:w-[1000px] tw:border-b tw:border-[#2B3B4C] tw:bg-[#0B1E33] tw:px-6 tw:py-3 tw:text-sm tw:text-white"
        >
            <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3">
                <h2 className="tw:!m-0 tw:!text-sm tw:!font-semibold">Warm-up</h2>
                <span
                    className="tw:rounded tw:border tw:border-blue-300 tw:px-1.5 tw:py-0.5 tw:text-xs"
                    data-testid="warmup-state"
                >
                    {warmupStateLabel(warmup)}
                </span>
                <span className="tw:text-gray-200">
                    {eventLabel(warmup.warmup_event)} warm-up for {eventLabel(warmup.target_event)}
                </span>
                <WarmupHoldoutNote holdout={entry?.holdout} />
                <button
                    type="button"
                    aria-expanded={expanded}
                    className="tw:text-xs tw:text-blue-400 tw:hover:underline"
                    onClick={() => setOpen(!expanded)}
                >
                    {expanded ? 'Hide' : 'Show'}
                </button>
            </div>

            {expanded && (
                <div className="tw:mt-3 tw:flex tw:flex-col tw:gap-3">
                    <p className="tw:!m-0 tw:text-gray-200">{warmupStateNote(warmup)}</p>

                    {!warmup.feature_enabled && (
                        <p className="tw:!m-0 tw:text-yellow-300" role="status" data-testid="warmup-switched-off">
                            The pixel warm-up is switched off for your licence. This warm-up stays as it is. You can
                            check it again with Meta or cancel it.
                        </p>
                    )}
                    {warmup.feature_enabled && warmup.halted && (
                        <p className="tw:!m-0 tw:text-yellow-300" role="status" data-testid="warmup-halted">
                            Automatic steps are paused for all campaigns right now. You can check this warm-up again
                            with Meta or cancel it.
                        </p>
                    )}
                    {warmup.campaign_paused && (
                        <p className="tw:!m-0 tw:text-yellow-300" data-testid="warmup-campaign-paused">
                            {PAUSED_CAMPAIGN_NOTE}
                        </p>
                    )}

                    <div data-testid="warmup-progress" className="tw:flex tw:flex-col tw:gap-0.5">
                        {progress.count !== null && progress.count !== undefined ? (
                            <span>
                                {progress.count} of {progress.threshold} {eventLabel(warmup.target_event)} events in the
                                last 7 days
                                {progress.count_checked_at ? ` (counted ${formatWhen(progress.count_checked_at)})` : ''}
                            </span>
                        ) : (
                            <span>
                                Not counted yet. The threshold is {progress.threshold} events in the last 7 days.
                            </span>
                        )}
                        {progress.last_checked_at && (
                            <span className="tw:text-gray-200" data-testid="warmup-last-check">
                                Last check {formatWhen(progress.last_checked_at)}
                                {checkReasonText(progress.reason) ? `: ${checkReasonText(progress.reason)}` : ''}
                                {olderCount ? ' That check could not count, so the count above is older.' : ''}
                            </span>
                        )}
                    </div>

                    {warmup.state === 'awaiting_confirmation' && (
                        <p className="tw:!m-0" data-testid="warmup-proposal">
                            {proposalText(warmup.proposal)}
                        </p>
                    )}
                    {(warmup.state === 'failed' || warmup.state === 'expanding') && (
                        <p className="tw:!m-0 tw:text-gray-200" data-testid="warmup-expansion">
                            Attempt {warmup.expansion.attempts} of {warmup.expansion.max_attempts}
                            {failure ? `. ${failure}` : ''}
                        </p>
                    )}

                    <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3">
                        {warmup.can.confirm && (
                            <Button size="sm" disabled={panel.busy} onClick={() => setConfirming(true)}>
                                Expand now
                            </Button>
                        )}
                        {warmup.can.decline && (
                            <Button
                                size="sm"
                                variant="secondary"
                                disabled={panel.busy}
                                onClick={() => void panel.decline({})}
                            >
                                Keep warming
                            </Button>
                        )}
                        {warmup.can.retry && (
                            <Button size="sm" disabled={panel.busy} onClick={() => void panel.retry({})}>
                                {warmup.state === 'expanding' ? 'Resume the expansion' : 'Try the expansion again'}
                            </Button>
                        )}
                        {warmup.can.cancel && (
                            <Button
                                size="sm"
                                variant="secondary"
                                disabled={panel.busy}
                                onClick={() => {
                                    setChoiceRequired(false)
                                    setCancelling(true)
                                }}
                            >
                                Cancel the warm-up
                            </Button>
                        )}
                        {warmup.can.set_mode && (
                            <span className="tw:flex tw:items-center tw:gap-2">
                                <span className="tw:text-gray-200">Mode</span>
                                <Select
                                    value={warmup.mode}
                                    onValueChange={(mode: string) => void panel.setMode({ mode })}
                                    disabled={panel.busy}
                                >
                                    <SelectTrigger aria-label="Mode" className="tw:w-[330px]">
                                        <SelectValue>
                                            {(MODE_LABELS as Record<string, string>)[warmup.mode] || warmup.mode}
                                        </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent className="tw:bg-primary tw:border-slate-700">
                                        {Object.values(MODES)
                                            .filter((mode) => mode !== MODES.AUTOMATED || warmup.automated_allowed)
                                            .map((mode) => (
                                                <SelectItem key={mode} value={mode} handleDelete={() => {}}>
                                                    {(MODE_LABELS as Record<string, string>)[mode]}
                                                </SelectItem>
                                            ))}
                                    </SelectContent>
                                </Select>
                            </span>
                        )}
                        {!warmup.can.set_mode && (
                            <span className="tw:text-gray-200" data-testid="warmup-mode">
                                Mode: {(MODE_LABELS as Record<string, string>)[warmup.mode] || warmup.mode}
                            </span>
                        )}
                        {!warmup.automated_allowed && (
                            <span className="tw:text-xs tw:text-gray-200" data-testid="warmup-automated-unavailable">
                                {AUTOMATED_NOT_ALLOWED_NOTE}
                                {warmup.mode === MODES.AUTOMATED
                                    ? ' This warm-up proposes the expansion for you to confirm.'
                                    : ''}
                            </span>
                        )}
                        {warmup.can.recheck && mayRecheck && warmup.warmup_ad_set && (
                            <button
                                type="button"
                                className="tw:text-xs tw:text-blue-400 tw:hover:underline"
                                disabled={panel.busy}
                                onClick={() => void panel.recheck(warmup.warmup_ad_set.id)}
                            >
                                Check the warm-up ad set again
                            </button>
                        )}
                    </div>

                    {openAttention.length > 0 && (
                        <div data-testid="warmup-attention">
                            <h3 className="tw:!m-0 tw:!mb-1 tw:!text-sm tw:!font-semibold">Needs your attention</h3>
                            <ul className="tw:!m-0 tw:!p-0 tw:list-none">
                                {openAttention.map((item: any) => {
                                    const adSetId = adSetIdOf(item.ad_set_external_id)
                                    return (
                                        <li
                                            key={`${item.kind}-${item.ad_set_external_id || 'campaign'}`}
                                            className="tw:flex tw:flex-wrap tw:gap-3 tw:py-1"
                                        >
                                            <span>
                                                {item.name ? `${item.name}: ` : ''}
                                                {item.text || ATTENTION_FALLBACK_TEXT}
                                            </span>
                                            {item.ad_set_external_id && (
                                                <a
                                                    className="tw:text-blue-400 tw:hover:underline"
                                                    href={metaAdSetLink(item.ad_set_external_id)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Open in Meta Ads Manager
                                                </a>
                                            )}
                                            {adSetId && warmup.can.recheck && mayRecheck && (
                                                <button
                                                    type="button"
                                                    className="tw:text-blue-400 tw:hover:underline"
                                                    disabled={panel.busy}
                                                    onClick={() => void panel.recheck(adSetId)}
                                                >
                                                    Check again
                                                </button>
                                            )}
                                        </li>
                                    )
                                })}
                            </ul>
                        </div>
                    )}

                    <WarmupHeldRows
                        rows={warmup.held_rows}
                        locked={running}
                        canRemove={warmup.can.remove_held_rows}
                        canRecheck={warmup.can.recheck && mayRecheck}
                        busy={panel.busy}
                        formatAmount={formatAmount}
                        editHref={editHref}
                        onSaveBudget={(adSetId, budget, expectedMinor) =>
                            void panel.changeHeldBudget(adSetId, budget, expectedMinor)
                        }
                        onRemove={setRemoving}
                        onRecheck={(adSetId) => void panel.recheck(adSetId)}
                        onEdit={(href) => navigate(href)}
                    />

                    <WarmupHistory
                        decisions={panel.decisions}
                        loading={panel.decisionsLoading}
                        onOpen={() => void panel.showHistory()}
                    />
                </div>
            )}

            <ConfirmationDialog
                isOpen={confirming}
                onClose={() => setConfirming(false)}
                onConfirm={async () => {
                    await panel.confirm({})
                    setConfirming(false)
                }}
                title="Expand the campaign"
                description="Turba creates your audience ad sets, pauses the warm-up ad set and then switches the new ad sets on. The warm-up ad set and the new ad sets never run together."
                confirmText="Expand now"
                variant="default"
                isProcessing={panel.busy}
            />
            <ConfirmationDialog
                isOpen={!!removing}
                onClose={() => setRemoving(null)}
                onConfirm={async () => {
                    if (removing) await panel.removeHeldRow(removing.id)
                    setRemoving(null)
                }}
                title="Remove the audience ad set"
                description={`${removing?.name || 'This audience ad set'} will not be launched by the expansion. If it already exists on Meta it is archived. You can add the audience again after the warm-up.`}
                confirmText="Remove"
                isProcessing={panel.busy}
            />
            <WarmupCancelDialog
                open={cancelling}
                choiceRequired={warmupNeedsChoice}
                processing={panel.busy}
                onClose={() => setCancelling(false)}
                onConfirm={async (choice) => {
                    const outcome = await panel.cancel(choice)
                    if (outcome.choiceRequired) {
                        setChoiceRequired(true)
                        return
                    }
                    setCancelling(false)
                }}
            />
        </section>
    )
}

export default WarmupPanel
